import React from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material"
import {
  Download as DownloadIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Pagination } from "swiper/modules"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import useLang from "../../../hooks/useLang"

interface ImageDownloadModalProps {
  imageUrls: { id: number; src: string }[]
  open: boolean
  onClose: () => void
  title?: string
  actions?: React.ReactNode
  isUndimiss?: boolean
  downloadName?: string
}

const ImageDownloadModal: React.FC<ImageDownloadModalProps> = ({
  imageUrls,
  open,
  onClose,
  title,
  actions,
  isUndimiss,
  downloadName = "images",
}) => {
  const { getText } = useLang()
  const handleDownloadZip = async (imageUrl: { id: number; src: string }[]) => {
    const zip = new JSZip()
    for (const url of imageUrl) {
      try {
        const response = await fetch(url.src)
        const blob = await response.blob()
        zip.file(`${downloadName}_${url.id}.png`, blob)
      } catch (error) {
        console.error("Erreur lors de l'ajout de l'image dans le ZIP", error)
      }
    }
    zip.generateAsync({ type: "blob" }).then((zipBlob) => {
      saveAs(zipBlob, `${downloadName}.zip`)
    })
  }

  // Custom CSS for Swiper navigation
  const swiperStyle = `
    .swiper-button-next, .swiper-button-prev {
      background-color: rgba(255, 255, 255, 0.8);
      color: black;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }
    .swiper-button-next:after, .swiper-button-prev:after {
      font-size: 18px;
      font-weight: bold;
    }
    .swiper-button-next:hover, .swiper-button-prev:hover {
      background-color: rgba(255, 255, 255, 1);
    }
    .swiper-pagination-bullet-active {
      background-color: #4E4E4EFF;
    }
    .swiper-pagination-bullet {
     background-color: #B4B4B4FF;
    }
  `

  return (
    <>
      <style>{swiperStyle}</style>
      <Dialog
        open={open}
        onClose={() => {
          if (!isUndimiss) {
            onClose()
          }
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "white",
            borderRadius: "15px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <h4 style={{ color: "black", margin: 0 }}>
            {title || "Galerie d'images"}
          </h4>
          <IconButton
            onClick={() => {
              if (!isUndimiss) {
                onClose()
              }
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {/* Swiper carousel with built-in navigation */}
          <Swiper
            modules={[Navigation, Pagination]}
            pagination={{ clickable: true }}
            navigation={true}
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            {imageUrls.map((imageUrl, index) => (
              <SwiperSlide key={index}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    aspectRatio: "auto",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <img
                    src={imageUrl.src}
                    alt={`Slide ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                    }}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            style={{ backgroundColor: "red" }}
            onClick={() => {
              if (!isUndimiss) {
                onClose()
              }
            }}
          >
            {getText("Fermer")}
          </Button>
          {actions}
          <Button
            onClick={() => {
              if (!isUndimiss && imageUrls.length > 0) {
                handleDownloadZip(imageUrls)
              }
            }}
            color="success"
            variant="contained"
            style={{ backgroundColor: "green" }}
            startIcon={<DownloadIcon />}
          >
            {getText("Télécharger")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ImageDownloadModal
